import { Margin, usePDF } from "react-to-pdf";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Link } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { axiosInstance } from "../../../../utils/networking";

const PRIMARY = "#9D2362";
const CARD_BORDER = "1px solid #ececf0";

const statusLabels: { [key: string]: string } = {
  notice_sent: "Provider Notice Sent",
  pending_surveys: "Pending Surveys",
  completed: "Completed",
};

const statusColors: { [key: string]: string } = {
  notice_sent: "#3F51B5",
  pending_surveys: "#fdb814",
  completed: "#2e7d32",
};

// ---- Small presentational helpers -------------------------------------------

const SectionCard = ({ children, sx }: { children: any; sx?: any }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: "16px",
      border: CARD_BORDER,
      backgroundColor: "white",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SectionTitle = ({
  children,
  subtitle,
}: {
  children: any;
  subtitle?: string;
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: "#2c2c34",
        borderLeft: `4px solid ${PRIMARY}`,
        pl: 1.5,
        lineHeight: 1.25,
      }}
    >
      {children}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" sx={{ color: "#8a8a93", pl: 1.5, mt: 0.5 }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

const StatusChip = ({ status }: { status: string }) => {
  const color = statusColors[status] || "#9e9e9e";
  return (
    <Chip
      label={statusLabels[status] || status}
      size="small"
      sx={{
        backgroundColor: `${color}1f`,
        color: color,
        fontWeight: 600,
        borderRadius: "6px",
      }}
    />
  );
};

const CompletionBar = ({ rate }: { rate: number }) => {
  const pct = Math.round((rate || 0) * 100);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        justifyContent: "flex-end",
      }}
    >
      <Box sx={{ width: 64 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 7,
            borderRadius: 4,
            backgroundColor: "#eef0f2",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "#2e7d32",
              borderRadius: 4,
            },
          }}
        />
      </Box>
      <Typography variant="body2" sx={{ minWidth: 36, textAlign: "right" }}>
        {pct}%
      </Typography>
    </Box>
  );
};

const StatCard = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: any;
  accent: string;
}) => (
  <Paper
    elevation={0}
    sx={{
      flex: "1 1 150px",
      p: 2.5,
      borderRadius: "16px",
      border: CARD_BORDER,
      borderTop: `3px solid ${accent}`,
      backgroundColor: "white",
      textAlign: "center",
    }}
  >
    <Typography
      sx={{
        fontSize: "2rem",
        fontWeight: 800,
        color: "#2c2c34",
        lineHeight: 1.1,
      }}
    >
      {value}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: "#8a8a93",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
  </Paper>
);

const ChartCard = ({
  title,
  subtitle,
  height,
  children,
}: {
  title: string;
  subtitle?: string;
  height: number | string;
  children: any;
}) => (
  <SectionCard sx={{ flex: "1 1 380px", minWidth: 0 }}>
    <SectionTitle subtitle={subtitle}>{title}</SectionTitle>
    <Box sx={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </Box>
  </SectionCard>
);

// Lifecycle order so the Status column sorts by progress
// (notice sent → completed) rather than alphabetically.
const statusOrder: { [key: string]: number } = {
  notice_sent: 1,
  pending_surveys: 2,
  completed: 3,
};

// Shared, sortable detail table for both the provider and advisor drill-downs.
// The second column varies: "Referred By" (advisor) for providers, "Provider"
// for advisors — driven by secondLabel/secondField.
const ReferralDetailTable = ({
  rows,
  secondLabel,
  secondField,
}: {
  rows: any[];
  secondLabel: string;
  secondField: string;
}) => {
  const [sortKey, setSortKey] = useState("referral_date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const columns = [
    { key: "firm_name", label: "Client" },
    { key: secondField, label: secondLabel },
    { key: "referral_objective", label: "Service Type" },
    { key: "referral_date", label: "Date" },
    { key: "status", label: "Status" },
  ];

  const getSortValue = (row: any, key: string) => {
    if (key === "referral_date")
      return row.referral_date ? new Date(row.referral_date).getTime() : 0;
    if (key === "status")
      return statusOrder[row.status] !== undefined
        ? statusOrder[row.status]
        : -1;
    return (row[key] || "").toString().toLowerCase();
  };

  const sortedRows = [...rows].sort((a, b) => {
    const av = getSortValue(a, sortKey);
    const bv = getSortValue(b, sortKey);
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {columns.map((col) => (
            <TableCell
              key={col.key}
              sortDirection={sortKey === col.key ? sortDir : false}
            >
              <TableSortLabel
                active={sortKey === col.key}
                direction={sortKey === col.key ? sortDir : "asc"}
                onClick={() => handleSort(col.key)}
              >
                {col.label}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows.map((row: any) => (
          <TableRow key={row.referral_id}>
            <TableCell>
              {row.case_id ? (
                <Link
                  to={`/case?id=${row.case_id}`}
                  style={{ color: PRIMARY, fontWeight: 600 }}
                >
                  {row.firm_name}
                </Link>
              ) : (
                row.firm_name
              )}
            </TableCell>
            <TableCell>{row[secondField]}</TableCell>
            <TableCell>{row.referral_objective}</TableCell>
            <TableCell>
              {row.referral_date
                ? dayjs(row.referral_date).format("YYYY-MM-DD")
                : ""}
            </TableCell>
            <TableCell>
              <StatusChip status={row.status} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// One provider row in the "By Provider" table that expands to show the
// clients (cases) referred to that provider and who referred each.
const ProviderRow = ({
  provider,
  clientRows,
}: {
  provider: any;
  clientRows: any[];
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }}
      >
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontWeight: 600 }}>
            {provider.provider_name}
          </Typography>
          {provider.provider_category ? (
            <Typography variant="caption" sx={{ color: "#8a8a93" }}>
              {provider.provider_category}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell align="right">{provider.total}</TableCell>
        <TableCell align="right">{provider.distinct_clients}</TableCell>
        <TableCell align="right">{provider.completed}</TableCell>
        <TableCell align="right" sx={{ width: 160 }}>
          <CompletionBar rate={provider.completion_rate} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ paddingY: 0, border: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                m: 1,
                p: 2,
                backgroundColor: "#faf7f9",
                borderRadius: "12px",
                border: "1px solid #f0e6ec",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: PRIMARY, fontWeight: 700 }}
              >
                Clients &amp; Referrals ({clientRows.length})
              </Typography>
              <ReferralDetailTable
                rows={clientRows}
                secondLabel="Referred By"
                secondField="advisor_name"
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// One advisor row in the "By Advisor" table that expands to show the
// referrals that advisor made (which clients, to which providers).
const AdvisorRow = ({
  advisor,
  clientRows,
}: {
  advisor: any;
  clientRows: any[];
}) => {
  const [open, setOpen] = useState(false);
  const rate = advisor.total > 0 ? advisor.completed / advisor.total : 0;
  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }}
      >
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{advisor.advisor_name}</TableCell>
        <TableCell align="right">{advisor.total}</TableCell>
        <TableCell align="right">{advisor.completed}</TableCell>
        <TableCell align="right" sx={{ width: 160 }}>
          <CompletionBar rate={rate} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ paddingY: 0, border: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                m: 1,
                p: 2,
                backgroundColor: "#faf7f9",
                borderRadius: "12px",
                border: "1px solid #f0e6ec",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: PRIMARY, fontWeight: 700 }}
              >
                Referrals Made ({clientRows.length})
              </Typography>
              <ReferralDetailTable
                rows={clientRows}
                secondLabel="Provider"
                secondField="provider_name"
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const B2BReferralsReport = () => {
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const fetchReport = (start: Dayjs | null, end: Dayjs | null) => {
    setLoading(true);
    let url = `/reports/b2b_referrals`;
    const params: string[] = [];
    if (start) params.push(`startDate=${start.format("YYYY-MM-DD")}`);
    if (end) params.push(`endDate=${end.format("YYYY-MM-DD")}`);
    if (params.length > 0) url = url + `?` + params.join("&");
    axiosInstance
      .get(url)
      .then((response: any) => {
        setReport(response.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReport(null, null);
  }, []);

  const { toPDF, targetRef } = usePDF({
    filename: "B2B_Referral_Report.pdf",
    page: { margin: Margin.SMALL, format: "letter" },
    overrides: {
      canvas: {
        removeContainer: false,
      },
    },
  });

  const handleCsvDownload = () => {
    if (!report) return;
    let csvContent: any[] = [];
    let csvHeading = [
      "Client",
      "Provider",
      "Provider Category",
      "Referred By",
      "Service Type",
      "Referral Date",
      "Status",
      "Completed Date",
    ];
    csvContent.push(csvHeading);

    for (const row of report.rows) {
      let csvAnswer = [];
      csvAnswer.push(row.firm_name);
      csvAnswer.push(row.provider_name);
      csvAnswer.push(row.provider_category || "");
      csvAnswer.push(row.advisor_name);
      csvAnswer.push(row.referral_objective);
      csvAnswer.push(
        row.referral_date ? dayjs(row.referral_date).format("YYYY-MM-DD") : ""
      );
      csvAnswer.push(statusLabels[row.status] || row.status);
      csvAnswer.push(
        row.completed_at ? dayjs(row.completed_at).format("YYYY-MM-DD") : ""
      );
      csvContent.push(csvAnswer);
    }

    let csvString = ``;
    for (const row of csvContent) {
      let rowString = `\n`;
      for (const item of row) {
        rowString = rowString + `"${item}",`;
      }
      csvString = csvString + rowString;
    }

    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `b2b_referrals.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    axiosInstance.post("/export_log", {
      export_type: "b2b_referral_report",
      record_count: report.rows.length,
      filters: {
        report: "B2BReferrals",
        startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
        endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
      },
    });
  };

  const rangeLabel =
    startDate || endDate
      ? `${startDate ? startDate.format("MMM D, YYYY") : "Beginning"} – ${
          endDate ? endDate.format("MMM D, YYYY") : "Today"
        }`
      : "All time";

  const toolbar = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: "16px",
        border: CARD_BORDER,
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(date) => setStartDate(date)}
            disableFuture={true}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(date) => setEndDate(date)}
            shouldDisableDate={(date: Dayjs) =>
              startDate !== null && date < startDate
            }
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>
        <Button
          variant="contained"
          onClick={() => fetchReport(startDate, endDate)}
          sx={{ backgroundColor: PRIMARY, "&:hover": { backgroundColor: "#7A1B4D" } }}
        >
          Apply
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setStartDate(null);
            setEndDate(null);
            fetchReport(null, null);
          }}
        >
          Reset
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={() => {
            toPDF();
            axiosInstance.post("/export_log", {
              export_type: "report_pdf",
              record_count: report ? report.rows.length : 0,
              filters: {
                report: "B2BReferrals",
                startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
                endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
              },
            });
          }}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleCsvDownload}
        >
          CSV
        </Button>
      </Box>
    </Paper>
  );

  const pageSx = {
    backgroundColor: "#f6f7f9",
    p: { xs: 2, md: 3 },
    minHeight: "100%",
  };

  if (loading && !report) {
    return (
      <Box
        sx={{
          ...pageSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!report) {
    return <Box sx={pageSx}>{toolbar}</Box>;
  }

  const statusData = Object.keys(statusLabels)
    .map((status) => ({
      key: status,
      name: statusLabels[status],
      value: report.rows.filter((row: any) => row.status === status).length,
    }))
    .filter((entry) => entry.value > 0);

  const monthlyMap: { [key: string]: any } = {};
  report.rows.forEach((row: any) => {
    if (!row.referral_date) return;
    const month = dayjs(row.referral_date).format("YYYY-MM");
    if (!monthlyMap[month]) {
      monthlyMap[month] = { month: month, Completed: 0, "In Progress": 0 };
    }
    if (row.status === "completed") {
      monthlyMap[month].Completed++;
    } else {
      monthlyMap[month]["In Progress"]++;
    }
  });
  const monthlyData = Object.values(monthlyMap).sort((a: any, b: any) =>
    a.month.localeCompare(b.month)
  );

  const providerChartData = report.byProvider
    .slice(0, 10)
    .map((provider: any) => ({
      name: provider.provider_name,
      Total: provider.total,
      Completed: provider.completed,
    }));

  const advisorChartData = report.byAdvisor.map((advisor: any) => ({
    name: advisor.advisor_name,
    Total: advisor.total,
    Completed: advisor.completed,
  }));

  const rowsByProvider: { [key: string]: any[] } = {};
  report.rows.forEach((row: any) => {
    if (!rowsByProvider[row.provider_id]) rowsByProvider[row.provider_id] = [];
    rowsByProvider[row.provider_id].push(row);
  });

  const rowsByAdvisor: { [key: string]: any[] } = {};
  report.rows.forEach((row: any) => {
    const key = row.advisor_id || "unknown";
    if (!rowsByAdvisor[key]) rowsByAdvisor[key] = [];
    rowsByAdvisor[key].push(row);
  });

  const businessesServed = new Set(
    report.rows.map((row: any) => row.firm_id).filter(Boolean)
  ).size;

  const statCards = [
    { label: "Total Referrals", value: report.summary.total, accent: PRIMARY },
    { label: "Businesses Served", value: businessesServed, accent: "#3F51B5" },
    { label: "Completed", value: report.summary.completed, accent: "#2e7d32" },
    { label: "Pending", value: report.summary.pending, accent: "#fdb814" },
    {
      label: "Completion Rate",
      value: `${Math.round(report.summary.completion_rate * 100)}%`,
      accent: "#2e7d32",
    },
    {
      label: "Avg Days to Complete",
      value:
        report.summary.avg_days_to_completion !== null
          ? Math.round(report.summary.avg_days_to_completion * 10) / 10
          : "—",
      accent: "#9e9e9e",
    },
  ];

  const isEmpty = report.rows.length === 0;

  return (
    <Box sx={pageSx}>
      {toolbar}

      <Box ref={targetRef}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#2c2c34" }}>
            B2B Referral Report
          </Typography>
          <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5 }}>
            {rangeLabel}
          </Typography>
        </Box>

        {isEmpty ? (
          <SectionCard sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" sx={{ color: "#8a8a93" }}>
              No B2B referrals found for this period
            </Typography>
            <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
              Try widening the date range or resetting the filter.
            </Typography>
          </SectionCard>
        ) : (
          <Stack spacing={3}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {statCards.map((card) => (
                <StatCard
                  key={card.label}
                  label={card.label}
                  value={card.value}
                  accent={card.accent}
                />
              ))}
            </Box>

            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <ChartCard title="Referrals by Status" height={260}>
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={2}
                    label={(entry: any) => entry.value}
                    isAnimationActive={false}
                  >
                    {statusData.map((entry) => (
                      <Cell key={entry.key} fill={statusColors[entry.key]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ChartCard>

              <ChartCard title="Referrals by Month" height={260}>
                <BarChart
                  data={monthlyData}
                  margin={{ top: 20, bottom: 10, right: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="Completed"
                    stackId="a"
                    fill="#2e7d32"
                    isAnimationActive={false}
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="In Progress"
                    stackId="a"
                    fill="#fdb814"
                    isAnimationActive={false}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartCard>
            </Box>

            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <ChartCard title="Top Providers" height={280}>
                <BarChart
                  data={providerChartData}
                  margin={{ top: 20, bottom: 70, right: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="name"
                    interval={0}
                    angle={-30}
                    tick={{ fontSize: 10, textAnchor: "end" }}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="Total"
                    fill={PRIMARY}
                    isAnimationActive={false}
                    radius={[4, 4, 0, 0]}
                  >
                    <LabelList dataKey="Total" position="top" />
                  </Bar>
                  <Bar
                    dataKey="Completed"
                    fill="#2e7d32"
                    isAnimationActive={false}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartCard>

              <ChartCard title="By Advisor" height={280}>
                <BarChart
                  data={advisorChartData}
                  margin={{ top: 20, bottom: 70, right: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="name"
                    interval={0}
                    angle={-30}
                    tick={{ fontSize: 10, textAnchor: "end" }}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="Total"
                    fill={PRIMARY}
                    isAnimationActive={false}
                    radius={[4, 4, 0, 0]}
                  >
                    <LabelList dataKey="Total" position="top" />
                  </Bar>
                  <Bar
                    dataKey="Completed"
                    fill="#2e7d32"
                    isAnimationActive={false}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartCard>
            </Box>

            <SectionCard>
              <SectionTitle subtitle="Expand a provider to see its clients and who referred each">
                By Provider
              </SectionTitle>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>Provider</TableCell>
                    <TableCell align="right">Referrals</TableCell>
                    <TableCell align="right">Distinct Clients</TableCell>
                    <TableCell align="right">Completed</TableCell>
                    <TableCell align="right">Completion Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.byProvider.map((provider: any) => (
                    <ProviderRow
                      key={provider.provider_id}
                      provider={provider}
                      clientRows={rowsByProvider[provider.provider_id] || []}
                    />
                  ))}
                </TableBody>
              </Table>
            </SectionCard>

            <SectionCard>
              <SectionTitle subtitle="Expand an advisor to see the referrals they made">
                By Advisor
              </SectionTitle>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>Advisor</TableCell>
                    <TableCell align="right">Referrals Made</TableCell>
                    <TableCell align="right">Completed</TableCell>
                    <TableCell align="right">Completion Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.byAdvisor.map((advisor: any) => (
                    <AdvisorRow
                      key={advisor.user_id || "unknown"}
                      advisor={advisor}
                      clientRows={
                        rowsByAdvisor[advisor.user_id || "unknown"] || []
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </SectionCard>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default B2BReferralsReport;
